import { HTTPError } from 'ky';
import { z } from 'zod';

/**
 * Last-used sign-in method hint
 *
 * Remembers *which* sign-in method last succeeded in this browser, so the next visit can
 * show a subtle "Last used" chip next to that option. Returning users overwhelmingly reach
 * for the method they used before, and on a screen that offers five or six equally-weighted
 * options that recall is the difference between a one-tap sign-in and a guess.
 *
 * Privacy — the method is the ONLY thing stored. Never the email, phone, username, user id
 * or anything else that identifies a person: an auth screen is very often a shared, kiosk or
 * family device, and "somebody signed in with Google here" leaks nothing, whereas
 * "alice@example.com signed in with Google here" would leak a great deal.
 *
 * The hint stays advisory. It never reorders the options and never pre-selects or forces a
 * path, so a wrong or stale value costs the user nothing — and the next successful sign-in
 * corrects it.
 */

/**
 * Namespaced with the same `logto:` prefix the other `localStorage` consumer uses
 * (see `social-redirect-fallback-context.ts`), so a host page sharing this origin can tell
 * our keys apart from its own.
 */
const storageKey = 'logto:last-used-sign-in-method';

/**
 * The sign-in methods that map to a single, fixed option in the UI. Social providers are
 * open-ended (any connector a tenant enables), so they are keyed by connector target
 * instead — see {@link lastUsedSignInMethodStruct}.
 */
export const builtInSignInMethods = Object.freeze([
  'passkey',
  'password',
  'email-code',
  'phone-code',
  'sso',
] as const);

const lastUsedSignInMethodStruct = z.union([
  z.object({ type: z.enum(builtInSignInMethods) }),
  z.object({
    type: z.literal('social'),
    /**
     * The connector *target* (`google`, `github`, …) rather than the connector id: targets
     * are stable and human-readable, while ids are regenerated whenever an admin deletes
     * and re-adds a connector, which would silently drop everyone's hint.
     */
    target: z.string().min(1),
  }),
]);

export type LastUsedSignInMethod = z.infer<typeof lastUsedSignInMethodStruct>;

/**
 * Error codes that mean "the credential was accepted, but the interaction is not finished
 * yet" — a second factor is required, or the profile needs completing first.
 *
 * These arrive as HTTP errors, but the user has already proven the method at that point and
 * the final redirect happens on a later page (MFA, profile completion) that no longer knows
 * how they got there. Recording the hint only when the sign-in call returns a redirect would
 * therefore leave every MFA-enabled tenant without a hint forever.
 */
const credentialAcceptedErrorCodes: ReadonlySet<string> = new Set([
  'session.mfa.require_mfa_verification',
  'session.mfa.suggest_additional_mfa',
  'session.mfa.backup_code_required',
  'user.missing_mfa',
  'user.suggest_mfa',
  'user.missing_profile',
  'user.passkey_preferred',
]);

const errorCodeStruct = z.object({ code: z.string() });

const isCredentialAcceptedError = (error: unknown): boolean => {
  if (!(error instanceof HTTPError)) {
    return false;
  }

  // `error.data` is the pre-parsed Logto error body (see `use-error-handler`); parse rather
  // than cast so a differently-shaped body can never throw on the sign-in happy path.
  const { data } = errorCodeStruct.safeParse(error.data);

  return Boolean(data && credentialAcceptedErrorCodes.has(data.code));
};

/**
 * Record the sign-in method for the next visit.
 *
 * Silently does nothing when `localStorage` is unavailable: Safari's private mode and a
 * number of embedded webviews *throw* on access rather than returning `null`, and a cosmetic
 * hint must never be allowed to break a sign-in.
 */
export const persistLastUsedSignInMethod = (method: LastUsedSignInMethod): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(lastUsedSignInMethodStruct.parse(method)));
  } catch {
    // Storage unavailable, or quota exceeded — degrade to no hint.
  }
};

/**
 * Read the recorded sign-in method, or `undefined` when there is none, the stored value is
 * malformed (hand-edited, or written by an older/newer version of this schema), or storage
 * is unavailable. Callers treat all of those identically: show no hint.
 */
export const readLastUsedSignInMethod = (): LastUsedSignInMethod | undefined => {
  try {
    const raw = localStorage.getItem(storageKey);

    if (raw === null) {
      return undefined;
    }

    // `JSON.parse` throws on garbage; the surrounding `catch` covers it alongside the
    // storage-access failure, since both mean the same thing here.
    const { data } = lastUsedSignInMethodStruct.safeParse(JSON.parse(raw));

    return data;
  } catch {
    return undefined;
  }
};

/** Forget the recorded sign-in method. Idempotent, never throws. */
export const clearLastUsedSignInMethod = (): void => {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Storage unavailable — there is nothing we could have read back anyway.
  }
};

/**
 * Record `method` when the user's credential was accepted, given the `error` returned
 * alongside the sign-in API call (`undefined`/`null` when it simply succeeded).
 *
 * Call this immediately after the API call rather than next to the final `redirectTo`, so
 * the flows that continue into MFA or profile completion are covered too — see
 * {@link credentialAcceptedErrorCodes}.
 */
export const rememberLastUsedSignInMethod = (
  method: LastUsedSignInMethod,
  error?: unknown
): void => {
  if (error !== undefined && error !== null && !isCredentialAcceptedError(error)) {
    return;
  }

  persistLastUsedSignInMethod(method);
};
